import {
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
     FormControl,
    TextField,
    Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import BookByDishService from "../../services/BookByDishService";
import { FoodCategory } from "../../enums/FoodCatergory";
import {
    FilledButton,
    FormFieldGroup,
    FormLabel,
    StyledDateText,
} from "../../styles/commonStyles";
import EventBus from "../../EventBus";
import { EventBusEventsEnum } from "../../enums/EventBusEventsEnum";
import { CustomSnackbarTypes } from "../Common/CustomSnackBar";
import { BookByDishCountUnit } from "../../enums/BookByDishCountUnit";
import { countUnitForDish, dishCountUnit, isPlateDish, plateUnitLabel } from "../../utils/BookByDishPlate";

// ── Shared types ──────────────────────────────────────────────────────────────

export interface DishOption {
    id: number;
    name: string;
    dishType?: string;
    // Bread plate configuration; a plate dish is picked and priced in plates.
    plateSize?: number;
    plateUnit?: string;
}

export { dishCountUnit };

// Unit shown next to a count being picked for this dish.
const pickerCountUnit = (dish: DishOption | null, count: number): string | null =>
    isPlateDish(dish) ? plateUnitLabel(dish?.plateUnit, count) : dishCountUnit(dish?.dishType);

// Title of the count dialog, e.g. "Number of plates of Tawa Roti (8 pcs per plate)".
const pickerTitle = (dish: DishOption | null): string => {
    if (isPlateDish(dish)) {
        return `Number of ${plateUnitLabel(dish?.plateUnit, 2).toLowerCase()} of ${dish?.name} (${dish?.plateSize} pcs per ${plateUnitLabel(dish?.plateUnit, 1).toLowerCase()})`;
    }
    return dish?.dishType === FoodCategory.BREAD
        ? `Number of ${dish?.name ?? 'breads'}`
        : `Number of people to eat ${dish?.name ?? 'dish'}`;
};

// ── Fuzzy search helpers ──────────────────────────────────────────────────────

const collapseRepeats = (s: string): string => {
    if (!s) return s;
    let result = s[0];
    for (let i = 1; i < s.length; i++) {
        if (s[i] !== s[i - 1]) result += s[i];
    }
    return result;
};

const levenshtein = (a: string, b: string): number => {
    if (a === b) return 0;
    if (!a.length) return b.length;
    if (!b.length) return a.length;
    const prev = Array.from({ length: b.length + 1 }, (_, i) => i);
    const curr = new Array(b.length + 1).fill(0);
    for (let i = 1; i <= a.length; i++) {
        curr[0] = i;
        for (let j = 1; j <= b.length; j++) {
            const cost = a[i - 1] === b[j - 1] ? 0 : 1;
            curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
        }
        prev.splice(0, prev.length, ...curr);
    }
    return prev[b.length];
};

const typoThreshold = (query: string): number =>
    Math.min(Math.max(Math.floor(query.length / 4), 1), 2);

const isSubsequence = (pattern: string, text: string): boolean => {
    let pi = 0;
    for (let ti = 0; ti < text.length && pi < pattern.length; ti++) {
        if (text[ti] === pattern[pi]) pi++;
    }
    return pi === pattern.length;
};

const subsequenceMatchRatio = (pattern: string, text: string): number => {
    if (!pattern) return 1.0;
    let matched = 0;
    let ti = 0;
    for (let pi = 0; pi < pattern.length; pi++) {
        while (ti < text.length && text[ti] !== pattern[pi]) ti++;
        if (ti < text.length) { matched++; ti++; }
    }
    return matched / pattern.length;
};

const splitQueryWords = (query: string): string[] =>
    query.toLowerCase().split(/\s+/).map(w => collapseRepeats(w.trim())).filter(w => w.length >= 3);

const scoreDish = (
    normalizedQuery: string,
    originalQuery: string,
    queryWords: string[],
    searchText: string,
): number => {
    const words = searchText.split(/\s+/).filter(w => w.length > 0);
    const lengthTieBreaker = Math.min(Math.max(10000 - searchText.trim().length, 0), 9999);

    if (searchText.trim() === originalQuery || searchText.trim() === normalizedQuery) return 70000 + lengthTieBreaker;
    if (words[0] === originalQuery || words[0] === normalizedQuery) return 60000 + lengthTieBreaker;
    if (words[0]?.startsWith(originalQuery) || words[0]?.startsWith(normalizedQuery)) return 50000 + lengthTieBreaker;
    if (words.slice(1).some(w => w === originalQuery || w === normalizedQuery)) return 40000 + lengthTieBreaker;
    if (words.slice(1).some(w => w.startsWith(originalQuery) || w.startsWith(normalizedQuery))) return 30000 + lengthTieBreaker;
    if (searchText.includes(originalQuery) || searchText.includes(normalizedQuery)) return 20000 + lengthTieBreaker;

    const queryNoSpace = originalQuery.replace(/\s/g, '');
    const searchNoSpace = searchText.replace(/\s/g, '');
    if (queryNoSpace.length >= 3) {
        if (searchNoSpace === queryNoSpace) return 19000 + lengthTieBreaker;
        if (searchNoSpace.startsWith(queryNoSpace)) return 18000 + lengthTieBreaker;
        if (searchNoSpace.includes(queryNoSpace)) return 17000 + lengthTieBreaker;
    }

    const threshold = typoThreshold(originalQuery);
    for (const word of words) {
        const dist = levenshtein(originalQuery, word);
        if (dist === 0) continue;
        if (dist <= threshold) return (word === words[0] ? 13000 : 12000) + lengthTieBreaker;
    }

    if (words[0]?.length >= 3 && originalQuery.startsWith(words[0])) return 11000 + lengthTieBreaker;
    if (isSubsequence(normalizedQuery, searchText)) return 10000;
    if (subsequenceMatchRatio(normalizedQuery, searchText) >= 0.8) return 5000;

    for (const word of queryWords) {
        if (searchText.includes(word)) return 100;
        if (isSubsequence(word, searchText)) return 50;
        if (subsequenceMatchRatio(word, searchText) > 0.8) return 30;
    }

    return 0;
};

const scoreDishNameInQuery = (dishName: string, query: string): number => {
    const toWords = (value: string) =>
        value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').split(/\s+/).map(w => collapseRepeats(w)).filter(w => w.length >= 3);

    const queryWords = toWords(query);
    const nameWords = toWords(dishName);
    if (nameWords.length === 0 || nameWords.length > queryWords.length) return 0;

    const used = new Set<number>();
    let exactMatches = 0;
    let fuzzyMatches = 0;
    let allMatched = true;

    for (const nameWord of nameWords) {
        let matchIndex = queryWords.findIndex((qw, i) => !used.has(i) && qw === nameWord);
        if (matchIndex !== -1) { used.add(matchIndex); exactMatches++; continue; }
        matchIndex = queryWords.findIndex((qw, i) => !used.has(i) && levenshtein(nameWord, qw) <= typoThreshold(nameWord));
        if (matchIndex === -1) { allMatched = false; break; }
        used.add(matchIndex);
        fuzzyMatches++;
    }

    if (!allMatched) return 0;
    return 25000 + exactMatches * 1000 + nameWords.length * 300 - fuzzyMatches * fuzzyMatches * 200;
};

export const fuzzySearchDishes = (dishes: DishOption[], query: string): DishOption[] => {
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) return dishes;
    const normalized = collapseRepeats(trimmed);
    const queryWords = splitQueryWords(trimmed);
    const scored = dishes.map(dish => {
        const searchText = dish.name.toLowerCase().replace(/[()|,]/g, ' ');
        const s1 = scoreDish(normalized, trimmed, queryWords, searchText);
        const s2 = scoreDishNameInQuery(dish.name, trimmed);
        return { dish, score: Math.max(s1, s2) };
    }).filter(e => e.score > 0);
    scored.sort((a, b) => b.score - a.score);
    return scored.slice(0, 100).map(e => e.dish);
};

// ─────────────────────────────────────────────────────────────────────────────

const bookByDishService = new BookByDishService();

const showError = (message: string) => {
    EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, { message, type: CustomSnackbarTypes.ERROR });
};

// ── Component ─────────────────────────────────────────────────────────────────

interface DishPickerRowProps {
    dishes: DishOption[];
    areaId?: number;
    disabled?: boolean;
    onAdd: (dish: DishOption, count: number, price?: number, time?: number, countUnit?: BookByDishCountUnit) => void;
}

const DishPickerRow = ({ dishes, areaId, disabled, onAdd }: DishPickerRowProps) => {
    const [selectedDish, setSelectedDish] = useState<DishOption | null>(null);
    const [count, setCount] = useState<number>(1);
    const [pricePreview, setPricePreview] = useState<{ price: number; time: number } | null>(null);
    const [checkingPrice, setCheckingPrice] = useState(false);

    // BREAD / CURRY count dialog
    const [dishDialogOpen, setDishDialogOpen] = useState(false);
    const [dishDialogValue, setDishDialogValue] = useState<number>(1);

    // Count picker dialog (for all dish types)
    const [countPickerOpen, setCountPickerOpen] = useState(false);
    const [countPickerValue, setCountPickerValue] = useState<number>(1);

    // Auto-fetch price when dish / count / areaId change (deferred while dialog is open).
    useEffect(() => {
        if (!selectedDish || count < 1 || dishDialogOpen) {
            if (!dishDialogOpen) setPricePreview(null);
            return;
        }
        let cancelled = false;
        setCheckingPrice(true);
        setPricePreview(null);
        bookByDishService
            .getDishPriceAndTime(selectedDish.id, count, areaId, countUnitForDish(selectedDish))
            .then((res) => { if (!cancelled) setPricePreview({ price: res.price, time: res.time }); })
            .catch(() => { if (!cancelled) showError("Failed to fetch price"); })
            .finally(() => { if (!cancelled) setCheckingPrice(false); });
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDish?.id, count, areaId, dishDialogOpen]);

    const handleDishChange = (_e: any, value: DishOption | null) => {
        setSelectedDish(value);
        setPricePreview(null);
        if (value?.dishType === FoodCategory.BREAD || value?.dishType === FoodCategory.CURRY) {
            setDishDialogValue(1);
            setDishDialogOpen(true);
        }
    };

    const handleAdd = () => {
        if (!selectedDish) return;
        onAdd(selectedDish, count, pricePreview?.price, pricePreview?.time, countUnitForDish(selectedDish));
        // reset row
        setSelectedDish(null);
        setCount(1);
        setPricePreview(null);
    };

    return (
        <>
            {/* BREAD / CURRY count dialog */}
            <Dialog open={dishDialogOpen} onClose={() => setDishDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {pickerTitle(selectedDish)}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        type="number"
                        label={isPlateDish(selectedDish)
                            ? `Number of ${plateUnitLabel(selectedDish?.plateUnit, 2).toLowerCase()}`
                            : selectedDish?.dishType === FoodCategory.BREAD ? `Number of ${selectedDish?.name ?? 'breads'}` : 'Number of people'}
                        value={dishDialogValue === 0 ? '' : dishDialogValue}
                        onChange={(e) => {
                            const raw = e.target.value;
                            if (raw === '') { setDishDialogValue(0); }
                            else { const val = Number(raw); if (val >= 0) setDishDialogValue(val); }
                        }}
                        InputProps={{ inputProps: { min: 1 } }}
                        size="small"
                        sx={{ mt: 1, mb: 2 }}
                    />
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, maxHeight: '60vh', overflowY: 'auto' }}>
                        {Array.from({ length: 100 }, (_, i) => i + 1).map((num) => (
                            <Button
                                key={num}
                                variant={dishDialogValue === num ? 'contained' : 'outlined'}
                                size="small"
                                sx={{ minWidth: 44 }}
                                onClick={() => {
                                    setCount(num);
                                    setDishDialogValue(num);
                                    setDishDialogOpen(false);
                                }}
                            >
                                {num}
                            </Button>
                        ))}
                    </Box>
                    {/* Counts above 100 have no button — let the typed value be submitted. */}
                    {dishDialogValue > 100 && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                            <Button
                                variant="contained"
                                color="success"
                                sx={{ textTransform: 'none', boxShadow: 'none' }}
                                onClick={() => {
                                    setCount(dishDialogValue);
                                    setDishDialogOpen(false);
                                }}
                            >
                                Submit {dishDialogValue}
                            </Button>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            {/* Count picker dialog */}
            <Dialog open={countPickerOpen} onClose={() => setCountPickerOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Select Count
                    {pickerCountUnit(selectedDish, 2) ? ` (${pickerCountUnit(selectedDish, 2)})` : ""}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        type="number"
                        label="Count"
                        value={countPickerValue === 0 ? '' : countPickerValue}
                        onChange={(e) => {
                            const raw = e.target.value;
                            if (raw === '') { setCountPickerValue(0); }
                            else { const val = Number(raw); if (val >= 0) setCountPickerValue(val); }
                        }}
                        InputProps={{ inputProps: { min: 1 } }}
                        size="small"
                        sx={{ mt: 1, mb: 2 }}
                    />
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, maxHeight: '60vh', overflowY: 'auto' }}>
                        {Array.from({ length: 100 }, (_, i) => i + 1).map((num) => (
                            <Button
                                key={num}
                                variant={countPickerValue === num ? 'contained' : 'outlined'}
                                size="small"
                                sx={{ minWidth: 44 }}
                                onClick={() => {
                                    setCount(num);
                                    setCountPickerValue(num);
                                    setCountPickerOpen(false);
                                }}
                            >
                                {num}
                            </Button>
                        ))}
                    </Box>
                    {/* Counts above 100 have no button — let the typed value be submitted. */}
                    {countPickerValue > 100 && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                            <Button
                                variant="contained"
                                color="success"
                                sx={{ textTransform: 'none', boxShadow: 'none' }}
                                onClick={() => {
                                    setCount(countPickerValue);
                                    setCountPickerOpen(false);
                                }}
                            >
                                Submit {countPickerValue}
                            </Button>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            {/* Row */}
            <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: "10px", alignItems: { xs: "stretch", sm: "flex-end" }, flex: 1, minWidth: 0 }}>
                <FormFieldGroup style={{ flex: 1, minWidth: 0 }}>
                    <FormLabel>Dish</FormLabel>
                    <FormControl style={{ width: "100%", height: "48px" }}>
                        <Autocomplete
                            options={dishes}
                            getOptionLabel={(o) => o.name}
                            isOptionEqualToValue={(o, v) => o.id === v.id}
                            filterOptions={(options, state) => fuzzySearchDishes(options, state.inputValue)}
                            value={selectedDish}
                            onChange={handleDishChange}
                            disabled={disabled}
                            sx={{
                                height: "48px",
                                '& .MuiOutlinedInput-root': { padding: '2px 14px !important', height: '48px', alignItems: 'center' },
                                '& .MuiOutlinedInput-root input': { padding: '0 !important', fontSize: '14px' },
                            }}
                            renderOption={(props, option) => (
                                <li {...props} key={option.id}>{option.name}</li>
                            )}
                            renderInput={(params) => (
                                <StyledDateText {...params} label="" variant="outlined" />
                            )}
                        />
                    </FormControl>
                </FormFieldGroup>

                <Box sx={{ width: { xs: "100%", sm: "190px" }, flexShrink: 0 }}>
                    <FormFieldGroup style={{ width: "100%" }}>
                        <FormLabel>Count</FormLabel>
                        <FormControl style={{ width: "100%", height: "48px" }}>
                            <StyledDateText
                                type="text"
                                value={
                                    pickerCountUnit(selectedDish, count)
                                        ? `${count} (${pickerCountUnit(selectedDish, count)})`
                                        : count
                                }
                                InputProps={{ inputProps: { readOnly: true } }}
                                onClick={() => {
                                    if (selectedDish && !disabled) {
                                        setCountPickerValue(count);
                                        setCountPickerOpen(true);
                                    }
                                }}
                                fullWidth
                                variant="outlined"
                                label=""
                                sx={{
                                    height: '48px',
                                    '& .MuiOutlinedInput-root': { height: '48px', cursor: 'pointer' },
                                    '& input': { cursor: 'pointer' },
                                }}
                            />
                        </FormControl>
                    </FormFieldGroup>
                </Box>

                {/* Price / Time / Add Dish — auto-shown once price is fetched */}
                <Box sx={{ display: "flex", alignItems: "center", gap: "10px", flexShrink: 0, flexWrap: "wrap", alignSelf: { xs: "stretch", sm: "flex-end" }, height: { xs: "auto", sm: "48px" }, mb: "3px" }}>
                    {checkingPrice && (
                        <CircularProgress size={22} sx={{ color: 'primary.main' }} />
                    )}
                    {!checkingPrice && pricePreview && (
                        <>
                            <Box
                                sx={{
                                    height: "48px",
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    border: "1px solid",
                                    borderColor: "divider",
                                    borderRadius: "6px",
                                    px: "16px",
                                    whiteSpace: "nowrap",
                                    backgroundColor: "rgba(25,118,210,0.04)",
                                    minWidth: "100px",
                                }}
                            >
                                <Typography variant="caption" color="text.secondary">Time</Typography>
                                <Typography variant="body2" fontWeight={600} color="text.primary">
                                    {pricePreview.time} min
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    height: "48px",
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    border: "1px solid",
                                    borderColor: "divider",
                                    borderRadius: "6px",
                                    px: "16px",
                                    whiteSpace: "nowrap",
                                    backgroundColor: "rgba(25,118,210,0.04)",
                                    minWidth: "100px",
                                }}
                            >
                                <Typography variant="caption" color="text.secondary">Price</Typography>
                                <Typography variant="body2" fontWeight={600} color="text.primary">
                                    ₹{pricePreview.price}
                                </Typography>
                            </Box>
                            <FilledButton
                                onClick={handleAdd}
                                disabled={disabled}
                                style={{ whiteSpace: "nowrap", height: "48px" }}
                            >
                                Add Dish
                            </FilledButton>
                        </>
                    )}
                </Box>
            </Box>
        </>
    );
};

export default DishPickerRow;
